import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { TASKS_TIMEZONE } from '@/modules/common/helpers/taskDateTime.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { INCIDENTS_LIST_QUERY_KEY } from '@/modules/common/queries/incidents.ts';
import {
  PUBLIC_REGISTERS_LIST_QUERY_KEY,
  REGISTERS_LIST_QUERY_KEY,
} from '@/modules/common/queries/registers.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CUSTOM_CHARTS_DATA_QUERY_KEY,
  CUSTOM_CHARTS_LIST_QUERY_KEY,
} from '@/modules/admin-dashboard/queries/custom-charts.ts';
import {
  CreateTaskResultRequest,
  GetGraphicDataV1TasksGraphicDataGetParams,
  GetMonthStatisticsV1TasksMonthStatisticsGetParams,
  GetTaskDashboardCountsV2Response,
  GetTaskListV2Response,
  GetTaskListV2TasksGetParams,
  GetTaskPreviewV2Response,
  GetTaskPreviewV2TasksPreviewGetParams,
  GetTaskPublicAccessResponse,
  GetTaskPublicAccessV1TasksPublicAccessGetParams,
  GetTaskResponse,
  GetTaskResultV2Response,
  GetTasksByIncidentResponse,
  PublicAccessCreateTaskResultRequest,
  RegisterResult,
  TaskListMode,
} from 'compliance-api';

export const CURRENT_USER_TASKS_GRAPHIC_QUERY_KEY = 'vestra-compliance/common-tasks-graphic-data';
export const CURRENT_USER_TASKS_MONTH_STATISTIC_QUERY_KEY =
  'vestra-compliance/common-tasks-monthly-statistic';
export const CURRENT_USER_TASKS_LIST_QUERY_KEY = 'vestra-compliance/common-tasks-list';
export const CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY =
  'vestra-compliance/common-tasks-by-linked-incident-list';
export const CURRENT_USER_TASK_QUERY_KEY = 'vestra-compliance/employee-task';
export const CURRENT_USER_TASK_RESULT_QUERY_KEY = 'vestra-compliance/task-result';
export const CURRENT_USER_PUBLIC_TASK_QUERY_KEY = 'vestra-compliance/employee-public-task';
export const CURRENT_USER_TASKS_PREVIEW_QUERY_KEY = 'vestra-compliance/common-tasks-preview';
export const CURRENT_USER_TASKS_DASHBOARD_COUNTS_QUERY_KEY =
  'vestra-compliance/common-tasks-dashboard-counts';

type TasksListQueryParams = GetTaskListV2TasksGetParams & {
  filter_by_compliance_status?: RegisterResult;
  showArchived?: boolean;
};

interface GetTaskPreviewV2Params
  extends Omit<GetTaskPreviewV2TasksPreviewGetParams, 'limit'>,
    Pick<GetTaskPreviewV2TasksPreviewGetParams, 'limit'> {
  limit?: number;
}

export const useTasksMonthStatisticQuery = (
  payload: GetMonthStatisticsV1TasksMonthStatisticsGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  return useQuery({
    queryKey: [CURRENT_USER_TASKS_MONTH_STATISTIC_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getMonthStatisticsV1TasksMonthStatisticsGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useTasksListQuery = (payload: TasksListQueryParams, disabled = false) => {
  const businessId = useGetCurrentBusinessId();
  const { page, showArchived, ...data } = payload;

  return useInfiniteQuery<GetTaskListV2Response>({
    queryKey: [
      CURRENT_USER_TASKS_LIST_QUERY_KEY,
      JSON.stringify({
        ...data,
        showArchived: !!showArchived,
      }),
      businessId,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    refetchInterval: 1000 * 60 * 5, //5min
    refetchOnMount: 'always',
    queryFn: async ({ pageParam = 0 }) => {
      const resolvedPage =
        typeof pageParam === 'object' && pageParam !== null && 'page' in pageParam
          ? Number(pageParam.page)
          : Number(pageParam || page);
      const params = {
        ...data,
        page: resolvedPage,
        timezone: TASKS_TIMEZONE,
      };

      if (showArchived) {
        return (await api.v2.getDeletedTaskListV2TasksDeletedGet(params))
          .data as GetTaskListV2Response;
      }

      switch (payload.mode) {
        case TaskListMode.Todo:
          return (await api.v2.getTodoTaskListV2TasksTodoGet(params)).data as GetTaskListV2Response;
        case TaskListMode.Upcoming:
          return (await api.v2.getUpcomingTaskListV2TasksUpcomingGet(params))
            .data as GetTaskListV2Response;
        case TaskListMode.History:
          return (await api.v2.getHistoryTaskListV2TasksHistoryGet(params))
            .data as GetTaskListV2Response;
        case TaskListMode.Overdue:
          return (await api.v2.getOverdueTaskListV2TasksOverdueGet(params))
            .data as GetTaskListV2Response;
        case TaskListMode.AllTasks:
          return (await api.v2.getAllTaskListV2TasksAllGet(params)).data as GetTaskListV2Response;
        default:
          return (await api.v2.getTaskListV2TasksGet(params)).data;
      }
    },
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useTasksPreviewQuery = (payload: GetTaskPreviewV2Params, disabled = false) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetTaskPreviewV2Response>({
    queryKey: [CURRENT_USER_TASKS_PREVIEW_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3,
    refetchInterval: 1000 * 60 * 5,
    refetchOnMount: 'always',
    queryFn: async () => {
      const params = {
        ...payload,
        timezone: TASKS_TIMEZONE,
      };

      switch (payload.mode) {
        case TaskListMode.Todo:
          return (await api.v2.getTodoTaskPreviewV2TasksTodoPreviewGet(params))
            .data as GetTaskPreviewV2Response;
        case TaskListMode.Upcoming:
          return (await api.v2.getUpcomingTaskPreviewV2TasksUpcomingPreviewGet(params))
            .data as GetTaskPreviewV2Response;
        case TaskListMode.History:
          return (await api.v2.getHistoryTaskPreviewV2TasksHistoryPreviewGet(params))
            .data as GetTaskPreviewV2Response;
        case TaskListMode.Overdue:
          return (await api.v2.getOverdueTaskPreviewV2TasksOverduePreviewGet(params))
            .data as GetTaskPreviewV2Response;
        case TaskListMode.AllTasks:
          return (await api.v2.getAllTaskPreviewV2TasksAllPreviewGet(params))
            .data as GetTaskPreviewV2Response;
        case TaskListMode.Deleted:
          return (await api.v2.getDeletedTaskPreviewV2TasksDeletedPreviewGet(params))
            .data as GetTaskPreviewV2Response;
        default:
          return (await api.v2.getTaskPreviewV2TasksPreviewGet(params)).data;
      }
    },
    retry: 2,
    enabled: !disabled,
  });
};

export const useTaskDashboardCountsQuery = (disabled = false) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetTaskDashboardCountsV2Response>({
    queryKey: [CURRENT_USER_TASKS_DASHBOARD_COUNTS_QUERY_KEY, businessId],
    staleTime: 1000 * 60 * 60 * 3,
    refetchInterval: 1000 * 60 * 5,
    refetchOnMount: 'always',
    queryFn: async () => {
      const response = await api.v2.getTaskDashboardCountsV2TasksDashboardCountsGet({
        timezone: TASKS_TIMEZONE,
      });
      return response.data;
    },
    retry: 2,
    enabled: !disabled,
  });
};

export const useTasksListByLinkedIncidentQuery = (id: number, disabled = false) => {
  return useQuery<GetTasksByIncidentResponse>({
    queryKey: [CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY, id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getTasksByIncidentResultV1TasksByIncidentResultIncidentResultIdGet({
          incidentResultId: id,
        });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useTasksGraphicDataQuery = (
  payload: GetGraphicDataV1TasksGraphicDataGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  return useQuery({
    queryKey: [CURRENT_USER_TASKS_GRAPHIC_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getGraphicDataV1TasksGraphicDataGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useGetTaskQuery = (taskId: number, disabled = false) => {
  return useQuery<GetTaskResponse>({
    queryKey: [CURRENT_USER_TASK_QUERY_KEY, taskId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getTaskV1TasksIdGet({ id: taskId });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useGetTaskResultQuery = (resultId: number, disabled = false) => {
  return useQuery<GetTaskResultV2Response>({
    queryKey: [CURRENT_USER_TASK_RESULT_QUERY_KEY, resultId],
    staleTime: 1000 * 60 * 60 * 3,
    queryFn: async () => {
      const response = await api.v2.getTaskResultV2TasksResultIdGet({ id: resultId });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useGetPublicTaskQuery = (
  data: GetTaskPublicAccessV1TasksPublicAccessGetParams,
  disabled = false,
) => {
  return useQuery<GetTaskPublicAccessResponse>({
    queryKey: [CURRENT_USER_PUBLIC_TASK_QUERY_KEY, JSON.stringify(data)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getTaskPublicAccessV1TasksPublicAccessGet(data);
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useResolveTaskMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateTaskResultRequest) => {
      const response = await withErrorHandling(api.v1.createTaskResultV1TasksResultPost(payload), {
        loading: 'Submitting...',
        notificationDuration: 10000,
        success: 'Task completed and results submitted, thank you!',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([REGISTERS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([PUBLIC_REGISTERS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASK_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_MONTH_STATISTIC_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_GRAPHIC_QUERY_KEY]);
      queryClient.invalidateQueries([CUSTOM_CHARTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CUSTOM_CHARTS_DATA_QUERY_KEY]);
    },
  });
};

export const usePublicResolveTaskMutation = () => {
  return useMutation({
    mutationFn: async (payload: PublicAccessCreateTaskResultRequest) => {
      const response = await withErrorHandling(
        api.v1.publicAccessCreateTaskResultV1TasksPublicAccessResultPost(payload),
        {
          loading: 'Submitting...',
          notificationDuration: 10000,
          success: 'Task completed and results submitted, thank you!',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([REGISTERS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([PUBLIC_REGISTERS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASK_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_MONTH_STATISTIC_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_GRAPHIC_QUERY_KEY]);
    },
  });
};
